import React, { useState, useMemo, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle, AlertCircle, Sparkles, Flag, X, Loader2, RefreshCw,
  Database, Building2, TrendingDown, Layers, ShieldCheck, BookOpen,
  FileText, Lightbulb,
} from 'lucide-react';
import apiClient from '../../lib/apiClient';
import logger from '../../lib/logger';
import PoDetailModal from '../PoDetailModal/PoDetailModal';
import { ClassificationAgentTrigger } from '../ClassificationAgent';
import './Triage.css';

/**
 * Triage — "what should I look at next?"
 *
 * Merges two existing signal sources into one ranked queue:
 *   • Active Learning candidates (/api/classification/eval/active-learning)
 *     — items scored by uncertainty × dollar × open_flags × hallucination_hits
 *   • Hallucination flags (/api/classification/eval/hallucinations?unack=true)
 *     — deterministic rule hits (vendor mismatch, nontech-keyword-as-IT,
 *     confidence paradox, retired component predicted, component-id-not-in-catalog)
 *
 * One-click "Flag for review" creates a classification_flags row populated
 * with the suspect signals as flag_reason; the item then enters the
 * existing Flags queue and the rest of the loop (Reclassify → Apply →
 * Rule Candidates).
 *
 * Per CLASSIFICATION_NORTH_STAR.md: this is layer-2 of the Continuous
 * Learning Loop ("Refinement candidate surfacing") promoted to a
 * first-class navigation surface.
 */

function fmtCurrency(n) {
  const v = Number(n) || 0;
  if (v >= 1e9) return `$${(v / 1e9).toFixed(2)}B`;
  if (v >= 1e6) return `$${(v / 1e6).toFixed(2)}M`;
  if (v >= 1e3) return `$${(v / 1e3).toFixed(0)}K`;
  return `$${Math.round(v).toLocaleString()}`;
}

function fmtRelative(iso) {
  if (!iso) return '—';
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return '—';
  const seconds = Math.max(1, Math.round((Date.now() - t) / 1000));
  if (seconds < 60) return `${seconds}s ago`;
  const m = Math.round(seconds / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

function tierFromAmount(amount) {
  const v = Number(amount) || 0;
  if (v >= 100_000) return { key: 'S+', variant: 'critical' };
  if (v >=  50_000) return { key: 'S',  variant: 'high' };
  if (v >=  10_000) return { key: 'M',  variant: 'medium' };
  if (v >=   1_000) return { key: 'L',  variant: 'low' };
  return { key: 'T', variant: 'trivial' };
}

// Merge active-learning + hallucination feeds into one row-per-item shape.
// When the same item_id appears in both, combine reasons.
function mergeFeeds(activeLearning, hallucinations) {
  const byItem = new Map();

  // Pass 1: active-learning rows
  for (const c of activeLearning) {
    const reasons = [];
    const sb = c.score_breakdown || {};
    if (sb.uncertainty > 0) {
      reasons.push({
        kind: 'uncertainty',
        label: 'Low confidence',
        detail: c.component_confidence != null
          ? `engine is ${(c.component_confidence * 100).toFixed(0)}% confident`
          : 'unclassified',
        weight: sb.uncertainty,
      });
    }
    if (sb.flag_pressure > 0) {
      reasons.push({
        kind: 'flag_pressure',
        label: 'Operator-flagged',
        detail: `${c.open_flags} open flag${c.open_flags === 1 ? '' : 's'}`,
        weight: sb.flag_pressure,
      });
    }
    if (sb.hallucination_hit > 0) {
      reasons.push({
        kind: 'hallucination_signal',
        label: 'Hallucination signal',
        detail: `${c.halluc_hits} rule hit${c.halluc_hits === 1 ? '' : 's'}`,
        weight: sb.hallucination_hit,
      });
    }
    if (sb.dollar_weight >= 6) {
      reasons.push({
        kind: 'high_dollar',
        label: 'Tier S+',
        detail: `${fmtCurrency(c.line_total)} — every misclass costs`,
        weight: sb.dollar_weight,
      });
    }
    byItem.set(c.item_id, {
      item_id: c.item_id,
      item_description: c.item_description,
      supplier_name: c.supplier_name,
      line_total: c.line_total,
      current_component_id: c.tech_component_id,
      current_component_name: c.current_component_name,
      component_confidence: c.component_confidence,
      classification_method: c.classification_method,
      open_flags: c.open_flags,
      priority_score: Number(c.priority_score) || 0,
      sources: new Set([sb.pool || 'active_learning']),
      reasons,
      hallucination_flags: [],   // populated in pass 2
      // PO context — used by the "View PO" action to open PoDetailModal
      // with full sibling-line context. Both feeds return these now;
      // hallucination-only rows pick them up via LEFT JOIN on the
      // backend so they're populated for that path too.
      purchase_document_num: c.purchase_document_num || null,
      department_id: c.department_id || null,
      start_date: c.start_date || null,
    });
  }

  // Pass 2: merge in hallucination rule hits
  for (const h of hallucinations) {
    const itemId = parseInt(h.source_ref, 10);
    if (!Number.isFinite(itemId)) continue;
    const existing = byItem.get(itemId);
    if (existing) {
      existing.sources.add('hallucination');
      existing.hallucination_flags.push(h);
      existing.reasons.push({
        kind: `halluc_${h.rule_id}`,
        label: `Hallucination · ${h.rule_id.replace(/_/g, ' ')}`,
        detail: h.reason || h.rule_id,
        weight: h.rule_severity === 'critical' ? 12 : h.rule_severity === 'warning' ? 8 : 4,
        flag_id: h.flag_id,
      });
      // If the active-learning row was missing PO context (shouldn't
      // happen post-backend-update but defensive), the hallucination
      // join may have it. Fill in any nulls.
      if (!existing.purchase_document_num) existing.purchase_document_num = h.purchase_document_num || null;
      if (!existing.department_id)         existing.department_id         = h.department_id || null;
      if (!existing.start_date)            existing.start_date            = h.start_date || null;
    } else {
      // Hallucination-only row (the item wasn't in active-learning top-N)
      byItem.set(itemId, {
        item_id: itemId,
        item_description: h.item_description,
        supplier_name: h.vendor_name,
        line_total: 0,
        current_component_id: null,
        current_component_name: h.predicted_component_name,
        component_confidence: h.confidence,
        classification_method: null,
        open_flags: 0,
        priority_score: h.rule_severity === 'critical' ? 12 : h.rule_severity === 'warning' ? 8 : 4,
        sources: new Set(['hallucination']),
        reasons: [{
          kind: `halluc_${h.rule_id}`,
          label: `Hallucination · ${h.rule_id.replace(/_/g, ' ')}`,
          detail: h.reason || h.rule_id,
          weight: h.rule_severity === 'critical' ? 12 : h.rule_severity === 'warning' ? 8 : 4,
          flag_id: h.flag_id,
        }],
        hallucination_flags: [h],
        purchase_document_num: h.purchase_document_num || null,
        department_id: h.department_id || null,
        start_date: h.start_date || null,
      });
    }
  }

  return [...byItem.values()];
}

function StatsStrip({ stats, hallucStats, isLoading }) {
  // Render the tile structure immediately even while loading; each
  // tile shows a Loader2 spinner in place of the number until its
  // data lands. Avoids a misleading "0" / blanket "Loading…" wall.
  const hasAl = !!stats;
  const hasHall = !!hallucStats;
  const tile = (icon, value, label, sub, variant, loading) => {
    const showSpinner = loading && value == null;
    return (
      <div className={`tg-stat ${variant ? `tg-stat--${variant}` : ''}`}>
        {icon}
        <div>
          <div className="tg-stat__value">
            {showSpinner ? <Loader2 size={18} className="tg-spin" /> : value}
          </div>
          <div className="tg-stat__label">{label}</div>
          <div className="tg-stat__sub">{sub}</div>
        </div>
      </div>
    );
  };
  return (
    <div className="tg-stats">
      {tile(
        <Sparkles size={14} className="tg-stat__icon" />,
        hasAl ? (stats.low_conf ?? 0).toLocaleString() : null,
        'Low-confidence items',
        'component_confidence < 0.5',
        null,
        !hasAl,
      )}
      {tile(
        <Layers size={14} className="tg-stat__icon" />,
        hasAl ? (stats.unclassified ?? 0).toLocaleString() : null,
        'Unclassified',
        'tech_component_id IS NULL',
        null,
        !hasAl,
      )}
      {tile(
        <AlertTriangle size={14} className="tg-stat__icon" />,
        hasHall ? (hallucStats.unack ?? 0).toLocaleString() : null,
        'Hallucination flags',
        hasHall
          ? `${hallucStats.last_24h ?? 0} in last 24h · ${hallucStats.last_7d ?? 0} in 7d`
          : '—',
        'warning',
        !hasHall,
      )}
      {tile(
        <Flag size={14} className="tg-stat__icon" />,
        hasAl ? (stats.flagged ?? 0).toLocaleString() : null,
        'Already flagged',
        'Open user flags — surfaced in /classification/flags',
        'primary',
        !hasAl,
      )}
    </div>
  );
}

function ReasonChip({ reason }) {
  return (
    <span className={`tg-reason tg-reason--${reason.kind.startsWith('halluc') ? 'halluc' : reason.kind}`}>
      <span className="tg-reason__label">{reason.label}</span>
      <span className="tg-reason__detail">{reason.detail}</span>
    </span>
  );
}

function SuspectCard({ row, onFlag, onDismiss, busy }) {
  const tier = tierFromAmount(row.line_total);
  const hasHallucSource = row.sources.has('hallucination');
  const [poModalOpen, setPoModalOpen] = useState(false);
  const canViewPo = !!row.purchase_document_num;
  // Optional operator-supplied domain knowledge that travels with the
  // flag and gets prepended to the next Reclassify-with-AI prompt for
  // this item. Collapsed by default — most rows don't need a hint.
  const [hintOpen, setHintOpen] = useState(false);
  const [hint, setHint] = useState('');

  return (
    <article className={`tg-card tg-card--tier-${tier.variant}`}>
      <header className="tg-card__head">
        <div className="tg-card__head-left">
          <span className={`tg-tier tg-tier--${tier.variant}`}>{tier.key}</span>
          <span className="tg-card__amount">{fmtCurrency(row.line_total)}</span>
          {row.supplier_name && (
            <span className="tg-card__supplier" title="Supplier of record">
              <Building2 size={11} /> {row.supplier_name}
            </span>
          )}
          {canViewPo && (
            <button
              type="button"
              className="tg-card__po"
              onClick={() => setPoModalOpen(true)}
              title={`Open PO ${row.purchase_document_num} — full document context`}
            >
              <FileText size={11} /> PO {row.purchase_document_num}
            </button>
          )}
        </div>
        <div className="tg-card__head-right">
          <span className="tg-card__priority" title="Composite priority score">
            score {row.priority_score.toFixed(2)}
          </span>
          <span className="tg-card__meta tg-card__meta--mono" title={`procurement_raw_data.id = ${row.item_id}`}>
            item #{row.item_id}
          </span>
        </div>
      </header>

      <p className="tg-card__desc">
        {row.item_description?.slice(0, 240) || <em className="tg-muted">no description</em>}
        {row.item_description && row.item_description.length > 240 ? '…' : ''}
      </p>

      <section className="tg-card__split">
        <div className="tg-card__pane">
          <h3 className="tg-card__pane-title"><Database size={12} /> Current classification</h3>
          <dl className="tg-kv">
            <dt>Component</dt>
            <dd>{row.current_component_name || <em className="tg-muted">unclassified</em>}</dd>
            <dt>Confidence</dt>
            <dd>{row.component_confidence != null ? `${(row.component_confidence * 100).toFixed(0)}%` : '—'}</dd>
            <dt>Method</dt>
            <dd className="tg-mono">{row.classification_method || '—'}</dd>
          </dl>
        </div>
        <div className="tg-card__pane">
          <h3 className="tg-card__pane-title"><AlertTriangle size={12} /> Why this is suspect</h3>
          <div className="tg-card__reasons">
            {row.reasons.length === 0
              ? <em className="tg-muted">no signals — empty pool</em>
              : row.reasons.map((r, i) => <ReasonChip key={i} reason={r} />)}
          </div>
        </div>
      </section>

      {hintOpen && (
        <section className="tg-card__hint" aria-label="Domain hint">
          <label className="tg-card__hint-label">
            <Lightbulb size={11} /> Domain hint for the AI
            <span className="tg-card__hint-sub">
              Anything the engine can't infer from the description — acronym
              expansions, program context, vendor relationships. Gets
              prepended to the Haiku prompt on the next Reclassify-with-AI.
            </span>
          </label>
          <textarea
            className="tg-card__hint-input"
            rows={3}
            value={hint}
            maxLength={1000}
            placeholder='e.g. "BMMI = California Broadband Middle-Mile Initiative; this is a CDT/Caltrans interagency agreement, not commercial IT staffing."'
            onChange={(e) => setHint(e.target.value)}
          />
          <div className="tg-card__hint-meta">
            <span className={`tg-card__hint-count ${hint.length > 900 ? 'is-near-limit' : ''}`}>
              {hint.length} / 1000
            </span>
            <button
              type="button"
              className="tg-action tg-action--ghost"
              onClick={() => { setHint(''); setHintOpen(false); }}
            >
              Cancel
            </button>
          </div>
        </section>
      )}

      <footer className="tg-card__actions">
        <button
          className="tg-action tg-action--primary"
          onClick={() => onFlag(row, hint.trim() || null)}
          disabled={busy}
          title={hint.trim()
            ? 'Flag with your domain hint attached — Reclassify-with-AI will read it on the next pass.'
            : 'Create a classification_flags row pre-populated with these reasons. The item enters /classification/flags for the rest of the loop.'}
        >
          {busy ? <Loader2 size={12} className="tg-spin" /> : <Flag size={12} />}
          Flag{hint.trim() ? ' + hint' : ' for review'}
        </button>
        <button
          type="button"
          className={`tg-action ${hintOpen || hint.trim() ? 'tg-action--accent' : 'tg-action--ghost'}`}
          onClick={() => setHintOpen(o => !o)}
          disabled={busy}
          title="Attach optional domain knowledge so the next AI pass has context the engine doesn't"
        >
          <Lightbulb size={12} /> {hint.trim() ? 'Edit hint' : 'Add hint'}
        </button>
        <button
          className="tg-action tg-action--muted"
          onClick={() => onDismiss(row)}
          disabled={busy}
          title={hasHallucSource
            ? 'Acknowledge the hallucination rule hits on this item — they stop showing here.'
            : 'Hide this row from the current Triage view. Will reappear on next refresh if signals persist.'}
        >
          <X size={12} /> Dismiss{hasHallucSource ? ' (ack)' : ''}
        </button>
        <button
          type="button"
          className="tg-action tg-action--ghost"
          onClick={() => setPoModalOpen(true)}
          disabled={!canViewPo}
          title={canViewPo
            ? `Open PO ${row.purchase_document_num} in the standard document modal — see all sibling line items + supplier + totals before flagging`
            : 'No purchase document number on this row — cannot open PO view'}
        >
          <FileText size={12} /> View PO
        </button>
        <ClassificationAgentTrigger
          itemId={row.item_id}
          description={row.item_description}
          supplier={row.supplier_name}
          amount={row.line_total}
          componentName={row.component_name}
          componentId={row.tech_component_id}
          confidence={row.component_confidence}
          method={row.classification_method}
          documentNumber={row.purchase_document_num}
          departmentId={row.department_id}
          hint={row.reasons?.map(r => `${r.label}: ${r.detail}`).join('; ')}
          source="triage"
          variant="compact"
        />
      </footer>

      {poModalOpen && canViewPo && (
        <PoDetailModal
          documentNumber={row.purchase_document_num}
          departmentId={row.department_id}
          startDate={row.start_date}
          isOpen={poModalOpen}
          onClose={() => setPoModalOpen(false)}
        />
      )}
    </article>
  );
}

function Triage() {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState({
    source: 'all',     // 'all' | 'active_learning' | 'hallucination'
    minAmount: 0,
    limit: 50,
  });
  // Local hide-set for dismissed rows in the current session.
  const [dismissed, setDismissed] = useState(new Set());
  // Inline error when a background dismiss/ack fails and the row is restored.
  const [dismissError, setDismissError] = useState(null);

  const alQuery = useQuery({
    queryKey: ['triage', 'active-learning', filters.minAmount, filters.limit],
    queryFn: () => apiClient.get('/api/classification/eval/active-learning', {
      params: { limit: filters.limit, min_amount: filters.minAmount },
    }),
    staleTime: 30_000,
  });

  const hallQuery = useQuery({
    queryKey: ['triage', 'hallucinations'],
    queryFn: () => apiClient.get('/api/classification/eval/hallucinations', {
      params: { unack: 'true', limit: 100 },
    }),
    staleTime: 30_000,
  });

  const hallStatsQuery = useQuery({
    queryKey: ['triage', 'halluc-stats'],
    queryFn: () => apiClient.get('/api/classification/eval/hallucinations/stats'),
    staleTime: 60_000,
  });

  const merged = useMemo(() => {
    const al = alQuery.data?.candidates || [];
    const hl = hallQuery.data?.flags || [];
    return mergeFeeds(al, hl);
  }, [alQuery.data, hallQuery.data]);

  const filtered = useMemo(() => {
    let rows = merged.filter(r => !dismissed.has(r.item_id));
    if (filters.source !== 'all') {
      rows = rows.filter(r =>
        filters.source === 'hallucination'
          ? r.sources.has('hallucination')
          : !r.sources.has('hallucination') || r.sources.size > 1
      );
    }
    rows.sort((a, b) => b.priority_score - a.priority_score);
    return rows;
  }, [merged, filters.source, dismissed]);

  const flagMutation = useMutation({
    mutationFn: ({ row, hint }) => apiClient.post('/api/classification/flags', {
      item_id: row.item_id,
      flag_reason: composeFlagReason(row),
      flag_category: row.reasons.some(r => r.kind.startsWith('halluc')) ? 'wrong_vendor' : 'other',
      flag_severity: row.priority_score >= 10 ? 'critical' : 'warning',
      hint_text: hint || null,
    }),
    onSuccess: (_data, { row }) => {
      logger.ui.info('Triage', 'flagged', { item_id: row.item_id, score: row.priority_score });
      setDismissed(prev => new Set(prev).add(row.item_id));
      queryClient.invalidateQueries({ queryKey: ['triage'] });
    },
    onError: (err, { row }) => {
      logger.ui.error('Triage', 'flag_failed', { item_id: row.item_id, message: err?.message });
      // eslint-disable-next-line no-console
      console.warn('Flag failed:', err);
    },
  });

  const ackMutation = useMutation({
    mutationFn: (flagId) => apiClient.post(`/api/classification/eval/hallucinations/${flagId}/ack`, {}),
  });

  const handleFlag = useCallback((row, hint) => flagMutation.mutate({ row, hint }), [flagMutation]);

  const handleDismiss = useCallback(async (row) => {
    // Optimistic: hide the row immediately, ack in the background.
    setDismissed(prev => new Set(prev).add(row.item_id));
    setDismissError(null);
    if (row.sources.has('hallucination')) {
      // Ack every hallucination flag attached to this item, in parallel.
      const results = await Promise.allSettled(
        row.hallucination_flags.map(h => ackMutation.mutateAsync(h.flag_id))
      );
      queryClient.invalidateQueries({ queryKey: ['triage'] });
      const failed = results.filter(r => r.status === 'rejected');
      if (failed.length > 0) {
        // eslint-disable-next-line no-console
        console.warn('Ack failed:', failed.map(f => f.reason?.message));
        // Revert the optimistic hide so the row is back for a retry.
        setDismissed(prev => {
          const next = new Set(prev);
          next.delete(row.item_id);
          return next;
        });
        setDismissError(
          `Dismiss failed — ${failed.length} of ${results.length} flag acks did not go through; the row was restored.`
        );
      }
    }
  }, [ackMutation, queryClient]);

  const isLoading = alQuery.isLoading || hallQuery.isLoading;
  const error = alQuery.error || hallQuery.error;

  return (
    <div className="tg-page">
      <header className="tg-page__head">
        <div className="tg-page__title">
          <AlertTriangle size={22} />
          <div>
            <h1>Triage</h1>
            <p className="tg-page__sub">
              Items the engine itself flagged as suspicious — ranked by{' '}
              <code>uncertainty × dollar × open_flags × hallucination_hits</code>{' '}
              (see <code>classificationActiveLearning.js</code> WEIGHTS).
              One click moves a row into <code>/classification/flags</code> for
              the rest of the loop. Per CLASSIFICATION_NORTH_STAR.md
              Continuous Learning Loop, layer 2.
            </p>
          </div>
        </div>
        <div className="tg-page__principles">
          <span className="tg-pill"><BookOpen size={11} /> Layer 2 · refinement candidates</span>
          <span className="tg-pill"><ShieldCheck size={11} /> Already-labeled items excluded</span>
        </div>
      </header>

      <StatsStrip
        stats={alQuery.data?.stats}
        hallucStats={hallStatsQuery.data?.total}
        isLoading={isLoading}
      />

      <div className="tg-toolbar">
        <div className="tg-filter">
          <label className="tg-filter__label">Source</label>
          <select
            value={filters.source}
            onChange={e => setFilters(f => ({ ...f, source: e.target.value }))}
          >
            <option value="all">All sources</option>
            <option value="active_learning">Active learning only</option>
            <option value="hallucination">Hallucination rules only</option>
          </select>
        </div>
        <div className="tg-filter">
          <label className="tg-filter__label">Min amount</label>
          <select
            value={filters.minAmount}
            onChange={e => setFilters(f => ({ ...f, minAmount: Number(e.target.value) }))}
          >
            <option value="0">All amounts</option>
            <option value="1000">≥ $1K (Tier L+)</option>
            <option value="10000">≥ $10K (Tier M+)</option>
            <option value="50000">≥ $50K (Tier S+)</option>
            <option value="100000">≥ $100K (Tier S+)</option>
          </select>
        </div>
        <button
          className="tg-icon-btn tg-icon-btn--ghost"
          onClick={() => { alQuery.refetch(); hallQuery.refetch(); hallStatsQuery.refetch(); }}
          disabled={isLoading}
          title="Refresh"
        >
          <RefreshCw size={14} className={isLoading ? 'tg-spin' : ''} />
        </button>
      </div>

      <main className="tg-list">
        {isLoading && (
          <div className="tg-state"><Loader2 size={18} className="tg-spin" /> Loading triage candidates…</div>
        )}
        {error && (
          <div className="tg-state tg-state--error">
            <AlertCircle size={18} /> Failed: {error.message || String(error)}
          </div>
        )}
        {dismissError && (
          <div className="tg-state tg-state--error">
            <AlertCircle size={18} /> {dismissError}
          </div>
        )}
        {!isLoading && !error && filtered.length === 0 && (
          <div className="tg-state tg-state--empty">
            <ShieldCheck size={28} />
            <h3>Nothing to triage</h3>
            <p>
              No items match the current filters. Either the engine is clean
              right now, or your filters are too tight.
            </p>
          </div>
        )}
        {filtered.map(row => (
          <SuspectCard
            key={row.item_id}
            row={row}
            onFlag={handleFlag}
            onDismiss={handleDismiss}
            busy={flagMutation.isPending && flagMutation.variables?.row?.item_id === row.item_id}
          />
        ))}
      </main>

      <footer className="tg-page__foot">
        Showing {filtered.length} of {merged.length} suspect{merged.length === 1 ? '' : 's'}.
        Already-flagged items appear above with a flag-pressure boost; already-labeled (in eval set) items are excluded.
      </footer>
    </div>
  );
}

function composeFlagReason(row) {
  const reasonStrs = row.reasons.map(r => `${r.label} (${r.detail})`).join('; ');
  const sources = [...row.sources].join(' + ');
  return `Surfaced via Triage [source: ${sources}; score: ${row.priority_score.toFixed(2)}]. ${reasonStrs}`;
}

export default Triage;
